// Shared helpers for the Google sign-in round trip.
// localStorage (not sessionStorage) so the role / intended route survive closing the app.
const ROLE_KEY = "cc_signup_role";
const DEST_KEY = "cc_post_login_dest";
const ROLE_TTL_MS = 30 * 60 * 1000; // an old visit to "Become a Cleaner" must not change a later signup

// Cookie backup: some mobile browsers partition or clear localStorage across the OAuth redirect,
// which used to drop a cleaner's registration intent and create a customer account instead.
function writeRoleCookie(role) {
  document.cookie = `${ROLE_KEY}=${role}; max-age=1800; path=/; samesite=lax; secure`;
}
function readRoleCookie() {
  const m = document.cookie.match(new RegExp(`(?:^|; )${ROLE_KEY}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}
function clearRoleCookie() {
  document.cookie = `${ROLE_KEY}=; max-age=0; path=/; samesite=lax; secure`;
}

export function dashboardFor(user) {
  if (!user) return "/login";
  if (user.role === "cleaner") return user.onboarded === false ? "/cleaner/onboarding" : "/cleaner/dashboard";
  if (["admin", "super_admin"].includes(user.role)) return "/admin";
  return "/customer";
}

// A remembered destination from earlier browsing must never override the journey the user just
// chose (e.g. a stale "/customer" dest sending a brand-new cleaner into the customer area).
export function destFor(user, dest) {
  if (!dest) return dashboardFor(user);
  const role = user?.role;
  const customerArea = /^\/(customer|host|book)(\/|$|\?)/.test(dest);
  const cleanerArea = /^\/cleaner(\/|$|\?)/.test(dest);
  const adminArea = /^\/admin(\/|$|\?)/.test(dest);
  const mismatch =
    (role === "cleaner" && (customerArea || adminArea)) ||
    (role === "customer" && (cleanerArea || adminArea)) ||
    (["admin", "super_admin"].includes(role) && (customerArea || cleanerArea));
  return mismatch ? dashboardFor(user) : dest;
}

export function rememberIntent({ role, dest } = {}) {
  if (role) {
    localStorage.setItem(ROLE_KEY, `${role}|${Date.now()}`);
    writeRoleCookie(role);
    // A deliberately chosen signup journey wins over anything remembered earlier.
    if (!dest) localStorage.removeItem(DEST_KEY);
  }
  if (dest && dest !== "/login" && dest !== "/register") localStorage.setItem(DEST_KEY, dest);
}

function storedRole() {
  const raw = localStorage.getItem(ROLE_KEY);
  if (!raw) return null;
  const [role, ts] = raw.split("|");
  if (ts && Date.now() - Number(ts) > ROLE_TTL_MS) return null;
  return role || null;
}

export function takeIntent() {
  const role = storedRole() || readRoleCookie() || null;
  const dest = localStorage.getItem(DEST_KEY);
  localStorage.removeItem(ROLE_KEY);
  clearRoleCookie();
  localStorage.removeItem(DEST_KEY);
  return { role, dest };
}

export function startGoogleSignIn({ role, dest } = {}) {
  rememberIntent({ role, dest });
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
